import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon, LayoutDashboard, Briefcase, Users, FileText, LogOut, ArrowUpRight, Wrench } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

export const PublicLayout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const navLink = (to, label, testid) => {
    const isActive = location.pathname === to || (to === '/vagas' && location.pathname.startsWith('/vagas'));
    return (
      <Link
        to={to}
        data-testid={testid}
        onClick={() => setMobileMenuOpen(false)}
        className="px-3 py-2 text-sm font-medium tracking-tight transition-colors"
        style={{ color: isActive ? 'var(--text-primary)' : 'var(--text-muted)' }}
      >
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="sticky top-0 z-30"
        style={{
          background: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border-secondary)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
              <img
                src={theme === 'dark' ? '/images/logo-branco.png' : '/images/logo-azul.png'}
                alt="Rent a Squad"
                width="36"
                height="36"
                loading="eager"
                className="w-9 h-9 object-contain"
              />
              <div className="hidden sm:block leading-tight">
                <span
                  className="block text-base font-bold tracking-tight"
                  style={{ color: theme === 'dark' ? 'var(--text-primary)' : 'var(--accent-primary)' }}
                >
                  Rent a Squad
                </span>
                <span className="block text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)' }}>
                  by Arcano
                </span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navLink('/', 'Início', 'nav-home')}
              {navLink('/vagas', 'Vagas', 'nav-jobs')}
              {navLink('/acompanhar', 'Acompanhar', 'nav-track')}
              <a
                href="#contato"
                onClick={(e) => {
                  if (location.pathname === '/') {
                    e.preventDefault();
                    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="ml-3 px-4 h-10 inline-flex items-center gap-1.5 rounded-md text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
                data-testid="nav-contact"
              >
                Falar com especialista
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <button
                onClick={toggleTheme}
                className="theme-toggle ml-2"
                data-testid="theme-toggle"
                aria-label="Mudar tema"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </nav>

            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={toggleTheme}
                className="theme-toggle"
                aria-label="Mudar tema"
                data-testid="theme-toggle-mobile"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                className="p-2 rounded-lg"
                style={{ color: 'var(--text-muted)' }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div
              className="md:hidden py-4 border-t"
              style={{ borderColor: 'var(--border-secondary)' }}
            >
              <nav className="flex flex-col gap-1">
                {navLink('/', 'Início', 'nav-home-mobile')}
                {navLink('/vagas', 'Vagas', 'nav-jobs-mobile')}
                {navLink('/acompanhar', 'Acompanhar', 'nav-track-mobile')}
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mt-2 mx-3 px-4 h-11 inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-semibold"
                  style={{ background: 'var(--accent-primary)', color: 'white' }}
                  data-testid="nav-contact-mobile"
                >
                  Falar com especialista
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer
        className="mt-auto"
        style={{
          borderTop: '1px solid var(--border-secondary)',
          background: 'var(--bg-secondary)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <img
                  src={theme === 'dark' ? '/images/logo-branco.png' : '/images/logo-azul.png'}
                  alt="Rent a Squad"
                  width="32"
                  height="32"
                  loading="lazy"
                  className="w-8 h-8 object-contain"
                />
                <span
                  className="text-base font-bold tracking-tight"
                  style={{ color: theme === 'dark' ? 'var(--text-primary)' : 'var(--accent-primary)' }}
                >
                  Rent a Squad
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                Squads de tecnologia entregues com a metodologia Torke®. Um produto Arcano Projetos.
              </p>
            </div>
            <div>
              <p
                className="text-xs font-semibold tracking-[0.18em] uppercase mb-4"
                style={{ color: 'var(--text-faint)' }}
              >
                Navegação
              </p>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/" className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>Início</Link></li>
                <li><Link to="/vagas" className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>Vagas</Link></li>
                <li><Link to="/acompanhar" className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>Acompanhar candidatura</Link></li>
                <li><Link to="/politica-de-privacidade" className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>Política de Privacidade</Link></li>
              </ul>
            </div>
            <div>
              <p
                className="text-xs font-semibold tracking-[0.18em] uppercase mb-4"
                style={{ color: 'var(--text-faint)' }}
              >
                Contato
              </p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="https://wa.me/5535991743960" target="_blank" rel="noopener noreferrer" className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>WhatsApp +55 35 99174.3960</a></li>
                <li><a href="mailto:alex.philippe@arcanoprojetos.com" className="hover:opacity-80 break-all" style={{ color: 'var(--text-secondary)' }}>alex.philippe@arcanoprojetos.com</a></li>
                <li><a href="https://instagram.com/arcano.pcs" target="_blank" rel="noopener noreferrer" className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>@arcano.pcs</a></li>
              </ul>
            </div>
            <div>
              <p
                className="text-xs font-semibold tracking-[0.18em] uppercase mb-4"
                style={{ color: 'var(--text-faint)' }}
              >
                Arcano Projetos
              </p>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <a
                    href="https://arcanoprojetos.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:opacity-80"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    arcanoprojetos.com <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </li>
                <li><Link to="/admin" data-testid="admin-link" className="hover:opacity-80" style={{ color: 'var(--text-faint)' }}>Acesso administrativo</Link></li>
              </ul>
            </div>
          </div>

          <div
            className="mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs"
            style={{ borderTop: '1px solid var(--border-secondary)', color: 'var(--text-faint)' }}
          >
            <span>© {new Date().getFullYear()} Arcano Projetos de Tecnologia. Todos os direitos reservados.</span>
            <span>Torke® é uma metodologia proprietária Arcano.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const adminNavItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/vagas', label: 'Vagas', icon: Briefcase },
  { path: '/admin/candidaturas', label: 'Candidaturas', icon: Users },
  { path: '/admin/ferramentas', label: 'Ferramentas', icon: Wrench },
  { path: '/admin/formularios', label: 'Formulários', icon: FileText },
];

const SidebarInner = ({ theme, location, onNavigate, onLogout }) => {
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const adminData = (() => {
    try {
      return JSON.parse(localStorage.getItem('admin_data') || '{}');
    } catch {
      return {};
    }
  })();
  return (
    <>
      <div className="px-5 lg:px-6 py-5 lg:py-6" style={{ borderBottom: '1px solid var(--border-secondary)' }}>
        <Link to="/admin/dashboard" className="flex items-center gap-2.5" onClick={onNavigate}>
          <img
            src={theme === 'dark' ? '/images/logo-branco.png' : '/images/logo-azul.png'}
            alt="Rent a Squad"
            width="32"
            height="32"
            loading="eager"
            className="w-8 h-8 object-contain"
          />
          <div className="leading-tight">
            <span
              className="block text-sm font-bold tracking-tight"
              style={{ color: theme === 'dark' ? 'var(--text-primary)' : 'var(--accent-primary)' }}
            >
              Rent a Squad
            </span>
            <span className="block text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)' }}>
              Admin
            </span>
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 lg:px-4 py-5 space-y-1">
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.18em] px-3 mb-3"
          style={{ color: 'var(--text-faint)' }}
        >
          Gestão
        </p>
        {adminNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              onClick={onNavigate}
              className="relative flex items-center gap-3 px-3 lg:px-4 py-2.5 rounded-md text-sm font-medium transition-all"
              style={{
                background: active ? 'var(--bg-tertiary)' : 'transparent',
                color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              }}
            >
              {active && (
                <span
                  className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r"
                  style={{ background: 'var(--accent-primary)' }}
                />
              )}
              <Icon className="w-4 h-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="px-3 lg:px-4 py-4" style={{ borderTop: '1px solid var(--border-secondary)' }}>
        {adminData?.email && (
          <div className="px-3 mb-3">
            <p className="text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>
              {adminData.name || 'Administrador'}
            </p>
            <p className="text-[11px] truncate" style={{ color: 'var(--text-faint)' }}>
              {adminData.email}
            </p>
          </div>
        )}
        <button
          onClick={onLogout}
          data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors text-left hover:bg-[var(--bg-tertiary)]"
          style={{ color: 'var(--text-muted)' }}
        >
          <LogOut className="w-4 h-4" />
          Sair
        </button>
      </div>
    </>
  );
};

export const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    navigate('/admin', { replace: true });
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-primary)' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 xl:w-64 flex-col flex-shrink-0" style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-secondary)' }}>
        <SidebarInner theme={theme} location={location} onNavigate={() => {}} onLogout={handleLogout} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 flex flex-col" style={{ background: 'var(--bg-secondary)' }}>
            <SidebarInner theme={theme} location={location} onNavigate={() => setSidebarOpen(false)} onLogout={handleLogout} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 lg:h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 flex-shrink-0" style={{ borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)]" style={{ color: 'var(--text-muted)' }} onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-base sm:text-lg lg:text-xl font-semibold font-brand truncate" style={{ color: 'var(--text-primary)' }}>{title}</h1>
          </div>
          <button onClick={toggleTheme} className="theme-toggle flex-shrink-0" data-testid="admin-theme-toggle" aria-label="Mudar tema">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
};
